"use client";

import * as React from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";

import { Container } from "@/app/_components/layout/container";
import { pricingBlock } from "@/lib/content/homepage";
import { trackEvent } from "@/lib/analytics/dataLayer";
import { cn } from "@/lib/utils";

type Cadence = "monthly" | "yearly";

export function PricingTiers() {
  const [cadence, setCadence] = React.useState<Cadence>("monthly");

  return (
    <section
      id="pricing"
      aria-label="Preise"
      className="bg-background text-clarus-dark py-20 md:py-28"
    >
      <Container>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-display-lg text-clarus-dark">
            {pricingBlock.heading}
          </h2>
          <p className="text-subline mt-4 text-clarus-dark/80">
            {pricingBlock.subline}
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <div
            role="tablist"
            aria-label="Abrechnungszeitraum"
            className="relative inline-grid auto-cols-fr grid-flow-col items-center rounded-pill border-[1.5px] border-clarus-dark bg-white p-1"
          >
            {/* Sliding indicator — sits behind the tabs and translates
                horizontally between them. translate-x relies on the two
                tabs being equal width (auto-cols-fr above). */}
            <span
              aria-hidden="true"
              className={cn(
                "absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-pill bg-clarus-dark transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                cadence === "yearly" && "translate-x-full",
              )}
            />
            {(
              [
                ["monthly", pricingBlock.toggle.monthly],
                ["yearly", pricingBlock.toggle.yearly],
              ] as const
            ).map(([key, label]) => {
              const active = cadence === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setCadence(key);
                    trackEvent("billing_toggle", { billing_period: key });
                  }}
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                  }}
                  className={cn(
                    "relative z-10 rounded-pill px-3.5 py-1.5 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
                    active
                      ? "text-clarus-yellow"
                      : "text-clarus-dark hover:text-clarus-dark/70",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <ul className="mt-12 grid gap-6 md:grid-cols-3 md:items-stretch">
          {pricingBlock.tiers.map((tier) => {
            const isYearly = cadence === "yearly";
            const price = isYearly ? tier.yearlyTotal : tier.monthlyPrice;
            const strikePrice = isYearly ? tier.yearlyTotalBefore : null;
            const period = isYearly
              ? tier.cadence.replace("pro Monat", "pro Jahr")
              : tier.cadence;
            const featured = tier.featured;
            return (
              <li
                key={tier.name}
                className={cn(
                  "relative flex h-full flex-col rounded-card border-[1.5px] border-clarus-dark p-6 md:p-7",
                  "transition-transform duration-200 ease-[ease] hover:-translate-y-[3px] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                  featured
                    ? "bg-clarus-purple md:-mt-4 md:h-[calc(100%+1rem)] md:pt-[2.75rem]"
                    : "bg-white",
                )}
              >
                {/* Badge slot — reserved height in ALL cards so the title row
                    starts at the same Y across the grid. Only the featured
                    card actually renders the pill. */}
                <div className="mb-2 flex h-7 justify-center">
                  {featured && "badge" in tier && tier.badge ? (
                    <span className="inline-flex items-center rounded-pill border-[1.5px] border-clarus-dark bg-clarus-purple px-4 py-1 text-eyebrow uppercase text-clarus-dark">
                      {tier.badge}
                    </span>
                  ) : null}
                </div>

                <h3
                  className="text-clarus-dark"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 800,
                    fontSize: "1.375rem",
                  }}
                >
                  {tier.name}
                </h3>
                {/* Yearly: the un-discounted 12× annual price, struck through
                    and small, sits above the discounted annual total so the
                    saving (one free month) reads at a glance. */}
                {strikePrice && (
                  <p
                    className="mt-3 text-clarus-dark/45 line-through"
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 800,
                      fontSize: "1rem",
                    }}
                  >
                    {tier.currency}
                    {strikePrice}
                  </p>
                )}
                <div
                  className={cn(
                    "flex items-baseline gap-1 text-clarus-dark",
                    isYearly ? "mt-0.5" : "mt-3",
                  )}
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 900,
                  }}
                >
                  <span className="text-[2.5rem] leading-none">
                    {tier.currency}
                    {price}
                  </span>
                </div>
                <p
                  className="mt-2 text-clarus-dark/70"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.9375rem",
                  }}
                >
                  {period}
                </p>
                {/* Plan scope — website count and employee range as one row
                    of icon chips so both limits read as a unit, not as loose
                    lines under the price. Chips pick up the page yellow on
                    white cards and go white on the purple featured card. */}
                <ul
                  aria-label="Tarifumfang"
                  className="mt-4 flex flex-wrap gap-2"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    lineHeight: 1,
                  }}
                >
                  {tier.limits.map((limit) => (
                    <li
                      key={limit.label}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-clarus-dark px-2.5 py-1.5 text-clarus-dark",
                        featured ? "bg-white" : "bg-clarus-yellow",
                      )}
                    >
                      <Icon name={limit.icon} className="text-[16px]" />
                      {"srLabel" in limit && limit.srLabel ? (
                        <>
                          <span aria-hidden="true">{limit.label}</span>
                          <span className="sr-only">{limit.srLabel}</span>
                        </>
                      ) : (
                        limit.label
                      )}
                    </li>
                  ))}
                </ul>

                {/* Divider between price block and features */}
                <hr
                  aria-hidden="true"
                  className="mt-5 border-t-[1.5px] border-clarus-dark/15"
                />

                <ul
                  className="mt-5 flex-1 space-y-2.5 text-clarus-dark"
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "0.9375rem",
                  }}
                >
                  {tier.features.map((feat) => {
                    const isPlus = feat.icon === "plus";
                    return (
                      <li key={feat.label} className="flex items-start gap-3">
                        <span
                          aria-hidden="true"
                          className="mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-clarus-dark bg-clarus-green"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={4}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className={cn(
                              "text-clarus-dark",
                              isPlus ? "h-3 w-3" : "h-3.5 w-3.5",
                            )}
                          >
                            {isPlus ? (
                              <path d="M12 5v14M5 12h14" />
                            ) : (
                              <path d="M5 12 10 17 19 7" />
                            )}
                          </svg>
                        </span>
                        <span>{feat.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-6 flex">
                  <Link
                    href={tier.cta.href}
                    onClick={() =>
                      trackEvent("select_plan", {
                        plan: tier.name,
                        billing_period: isYearly ? "yearly" : "monthly",
                      })
                    }
                    {...(tier.cta.href.startsWith("http")
                      ? ({ target: "_blank", rel: "noopener noreferrer" } as const)
                      : {})}
                    className={cn(
                      "inline-flex h-12 w-full items-center justify-center gap-2 rounded-pill border-[1.5px] border-clarus-dark text-cta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2",
                      featured
                        ? "bg-clarus-dark text-clarus-yellow hover:bg-clarus-dark/85"
                        : "bg-transparent text-clarus-dark hover:bg-clarus-dark hover:text-clarus-yellow",
                    )}
                  >
                    {tier.cta.label}
                    {featured ? (
                      <Icon name="arrow_forward" className="text-[16px]" />
                    ) : null}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>

        <p
          className="mt-10 text-center text-clarus-dark/70"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.9375rem",
          }}
        >
          {pricingBlock.footnote} ·{" "}
          <Link
            href={pricingBlock.footnoteLinkHref}
            className="underline underline-offset-2 hover:text-clarus-dark"
          >
            {pricingBlock.footnoteLinkLabel}
          </Link>
        </p>
      </Container>
    </section>
  );
}
